/**
 * @file src/pages/invoice-creation-wizard/components/SellerSection.jsx
 *
 * What changed from the original:
 *  - useQueryClient import removed — was imported but never used
 *  - countrySubentity onBlur fixed:
 *    Before: handleNestedInputBlur("address", "province", ...)
 *    After:  handleNestedInputBlur("address", "countrySubentity", ...)
 *  - Address fields driven by config array to reduce JSX repetition
 */

import React, { useState } from "react";
import { motion } from "framer-motion";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import { ErrorMessage } from "components/ui/FormValidator";
import Icon from "components/AppIcon";

// ---------------------------------------------------------------------------
// Address field config
// ---------------------------------------------------------------------------

const ADDRESS_FIELDS = [
  {
    key: "street",
    label: "Street Address *",
    placeholder: "Enter street address",
  },
  {
    key: "buildingNumber",
    label: "Building Number *",
    placeholder: "Enter building number",
  },
  {
    key: "plotIdentification",
    label: "Plot Identification",
    placeholder: "Enter plot ID",
  },
  { key: "city", label: "City *", placeholder: "Enter city" },
  {
    key: "citySubdivision",
    label: "City Subdivision",
    placeholder: "Enter city subdivision",
  },
  {
    key: "countrySubentity",
    label: "Province / Country Subentity *",
    placeholder: "Enter province/country subentity",
  },
  {
    key: "postalCode",
    label: "Postal Code *",
    placeholder: "Enter postal code",
  },
];

// ---------------------------------------------------------------------------
// SellerSection
// ---------------------------------------------------------------------------

const SellerSection = ({
  data,
  onChange,
  onNestedChange,
  errors,
  shouldShowError,
  onFieldChange,
  onFieldBlur,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleInputChange = (field, value) => {
    onChange("seller", field, value);
    onFieldChange(`seller.${field}`, value);
  };

  const handleNestedInputChange = (section, field, value) => {
    onNestedChange("seller", section, field, value);
    onFieldChange(`seller.${section}.${field}`, value);
  };

  const handleInputBlur = (field, value) => {
    onFieldBlur(`seller.${field}`, value);
  };

  const handleNestedInputBlur = (section, field, value) => {
    onFieldBlur(`seller.${section}.${field}`, value);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-hero-cta border border-gray-100 dark:border-gray-700"
    >
      <div className="p-6">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-secondary-100 dark:bg-secondary-900/20 rounded-lg flex items-center justify-center">
              <Icon name="Building" size={20} className="text-secondary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground dark:text-white">
                Seller Information
              </h3>
              <p className="text-sm text-muted-foreground dark:text-gray-300">
                Your company details for this invoice
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            onClick={() => setIsExpanded(!isExpanded)}
          />
        </div>

        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {/* Company Information */}
            <div>
              <h4 className="text-md font-medium text-foreground dark:text-white mb-4">
                Company Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Company Name *
                  </label>
                  <Input
                    type="text"
                    value={data?.registrationName ?? ""}
                    onChange={(e) =>
                      handleInputChange("registrationName", e.target.value)
                    }
                    onBlur={(e) =>
                      handleInputBlur("registrationName", e.target.value)
                    }
                    placeholder="Enter company name"
                    className={
                      shouldShowError("seller.registrationName")
                        ? "border-error"
                        : ""
                    }
                  />
                  <ErrorMessage
                    error={errors("seller.registrationName")}
                    show={shouldShowError("seller.registrationName")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    VAT Number *
                  </label>
                  <Input
                    type="text"
                    value={data?.vatNumber ?? ""}
                    onChange={(e) =>
                      handleInputChange("vatNumber", e.target.value)
                    }
                    onBlur={(e) => handleInputBlur("vatNumber", e.target.value)}
                    placeholder="3001234567890003"
                    className={
                      shouldShowError("seller.vatNumber") ? "border-error" : ""
                    }
                  />
                  <ErrorMessage
                    error={errors("seller.vatNumber")}
                    show={shouldShowError("seller.vatNumber")}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    CR Number
                  </label>
                  <Input
                    type="text"
                    value={data?.registrationNumber?.id ?? ""}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "registrationNumber",
                        "id",
                        e.target.value,
                      )
                    }
                    onBlur={(e) =>
                      handleNestedInputBlur(
                        "registrationNumber",
                        "id",
                        e.target.value,
                      )
                    }
                    placeholder="1010123456"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                    Registration Scheme
                  </label>
                  <Input
                    type="text"
                    value={data?.registrationNumber?.schemeId ?? ""}
                    onChange={(e) =>
                      handleNestedInputChange(
                        "registrationNumber",
                        "schemeId",
                        e.target.value,
                      )
                    }
                    onBlur={(e) =>
                      handleNestedInputBlur(
                        "registrationNumber",
                        "schemeId",
                        e.target.value,
                      )
                    }
                    placeholder="CRN"
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div>
              <h4 className="text-md font-medium text-foreground dark:text-white mb-4">
                Address Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ADDRESS_FIELDS.map(({ key, label, placeholder }) => (
                  <div key={key}>
                    <label className="block text-sm font-medium text-foreground dark:text-white mb-2">
                      {label}
                    </label>
                    <Input
                      type="text"
                      value={data?.address?.[key] ?? ""}
                      onChange={(e) =>
                        handleNestedInputChange("address", key, e.target.value)
                      }
                      onBlur={(e) =>
                        handleNestedInputBlur("address", key, e.target.value)
                      }
                      placeholder={placeholder}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* ZATCA Compliance Notice */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon
                  name="Info"
                  size={20}
                  className="text-blue-600 dark:text-blue-400 mt-0.5"
                />
                <div>
                  <h5 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                    Company Information
                  </h5>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    This information is pre-filled from your company profile.
                    You can modify it for this specific invoice if needed.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

export default SellerSection;
